import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import './index.css';
import '@stem/lab-engine/styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A minute, because the audience is on metered data and most of this
      // content changes weekly at most. Refetching a course tree on every tab
      // focus is somebody's airtime.
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: (attempt, error) => {
        const status = (error as { status?: number })?.status ?? 0;
        // Retrying a 402 or a 403 will produce the same 402 or 403. Only
        // network-level failures are worth a second attempt.
        if (status >= 400 && status < 500) return false;
        return attempt < 2;
      },
    },
  },
});

const tree = (
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
);

const container = document.getElementById('root')!;

/*
 * The public marketing routes are prerendered to static HTML at build time, so
 * a crawler and a slow phone both get content before any JavaScript runs.
 *
 * Hydrating requires the markup in the page to be the markup this route would
 * render, and two things conspire to make that easy to get wrong:
 *
 *  - the template ships `<div id="root"><!--app-html--></div>`, and a comment
 *    node makes `hasChildNodes()` true, so a naive check hydrates an empty root
 *    on the dev server and on every non-prerendered route;
 *  - `dist/index.html` is *both* the prerendered home page and the SPA fallback
 *    a static host serves for unmatched paths, so `/o/school/labs/x` arrives
 *    carrying the home page's HTML.
 *
 * Both cases end the same way: React throws a hydration error, discards the
 * markup and re-renders from scratch. Recoverable, and a wasted first paint on
 * every authenticated page load.
 *
 * So the prerenderer stamps the route it rendered, and hydration happens only
 * on an exact match. Anything else clears the container and mounts fresh —
 * which is what the SPA wanted in the first place.
 */
const prerenderedPath = container.dataset.prerendered;
const currentPath = window.location.pathname.replace(/(.)\/$/, '$1');

if (prerenderedPath && prerenderedPath.replace(/(.)\/$/, '$1') === currentPath) {
  hydrateRoot(container, tree);
} else {
  container.replaceChildren();
  createRoot(container).render(tree);
}
